import React from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { MailOutline } from '@material-ui/icons';
import { useMessagesV2Query } from './api';
import { MAX_SELECTED, getRangeParams, useTableState } from './tableState';
import useStatDrawers from './hooks/useStatDrawers';
import Toolbar from './components/Toolbar/Toolbar';
import EngagementCard from './components/EngagementCard/EngagementCard';
import MessageTable from './components/MessageTable/MessageTable';
import {
  Page,
  Tabs,
  Tab,
  Top,
  TopLeft,
  TitleRow,
  Title,
  Description,
  TableCard,
} from './AnalyticsV2.styled';

const TablePage = () => {
  const history = useHistory();
  const route = useRouteMatch();
  const {
    orgId,
    searchText,
    setSearchText,
    search,
    dateRange,
    setDateRange,
    statuses,
    setStatuses,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    selected,
    setSelected,
    showSelectedOnly,
    setShowSelectedOnly,
    selectedIds,
  } = useTableState();
  const { openTileDrawer, drawer } = useStatDrawers();

  const paging = { page: page + 1, size: rowsPerPage };
  const params = selectedIds
    ? { ids: selectedIds, ...paging }
    : {
        statuses: statuses.join(','),
        ...paging,
        ...(search && { search }),
        ...(dateRange && getRangeParams(dateRange)),
      };
  // With no status ticked in Filter there is nothing to list.
  const isQueryEnabled = !!selectedIds || statuses.length > 0;
  const { data, isLoading, isFetching, isError, refetch } = useMessagesV2Query(
    orgId,
    params,
    { enabled: isQueryEnabled }
  );
  const messages = isQueryEnabled ? data?.result ?? [] : [];
  const total = isQueryEnabled ? data?.pagination.total ?? 0 : 0;

  // Unselected rows can't be ticked once MAX_SELECTED are selected (their
  // checkboxes are disabled), so this never selects more than that.
  const toggleRow = message => {
    const next = new Map(selected);
    if (next.has(message.id)) next.delete(message.id);
    else next.set(message.id, message);
    setSelected(next);
  };

  const togglePage = (pageMessages, isPageSelected) => {
    const next = new Map(selected);
    for (const message of pageMessages) {
      if (isPageSelected) next.delete(message.id);
      else next.set(message.id, message);
    }
    setSelected(next);
  };

  return (
    <Page fitsWindow>
      <Tabs>
        <Tab>
          <MailOutline fontSize="small" /> Messages
        </Tab>
      </Tabs>
      <Top>
        <TopLeft>
          <TitleRow>
            <Title>
              <MailOutline /> Messages
            </Title>
            <Description>
              Select one or more campaigns to view individual or combined
              analytics.
            </Description>
          </TitleRow>
          <Toolbar
            searchText={searchText}
            onSearchTextChange={setSearchText}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            statuses={statuses}
            onStatusesChange={setStatuses}
            selectedCount={selected.size}
            onClearSelection={() => setSelected(new Map())}
            areFiltersDisabled={showSelectedOnly}
          />
        </TopLeft>
        <EngagementCard
          messages={selected.size > 0 ? [...selected.values()] : messages}
          selectedCount={selected.size}
          // With a selection the graph uses rows already loaded, so it only
          // waits for the table when nothing is selected.
          isLoading={selected.size === 0 && isFetching}
          onViewStats={() => history.push(`${route.url}/stats`)}
        />
      </Top>
      <TableCard>
        <MessageTable
          messages={messages}
          isLoading={isLoading}
          isFetching={isFetching}
          isError={isError}
          total={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          selected={selected}
          maxSelected={MAX_SELECTED}
          onToggleRow={toggleRow}
          onTogglePage={togglePage}
          showSelectedOnly={showSelectedOnly}
          onShowSelectedOnlyChange={setShowSelectedOnly}
          onTileClick={(message, tileKey) =>
            openTileDrawer(message, tileKey, 'all')
          }
          onOpenMessage={message =>
            history.push(`${route.url}/message/${message.id}`)
          }
          onChanged={refetch}
        />
      </TableCard>
      {drawer}
    </Page>
  );
};

export default TablePage;
